import { inject, Injectable, TemplateRef } from '@angular/core';
import { CONFIG_TOKEN } from '@pxl/models/common';
import { BrowserService, LoginService, TermsOfUseService } from '@pxl/models/contracts';
import {
    TermOfUseModel,
    TermsOfUseForUserModel,
    TermsOfUseModel,
    TermsOfUseService as TermsOfUseBackendService,
} from '@pxl/shared/generated';
import { LanguageService } from '@pxl/shared/translation';
import { firstValueFrom, Observable } from 'rxjs';

@Injectable()
export class DefaultTermsOfUseService implements TermsOfUseService {
    private readonly currentTemplate: {
        header: TemplateRef<any>;
        footer: TemplateRef<any>;
    } = {
        header: null,
        footer: null,
    };

    private readonly termsOfUseBackendService = inject(TermsOfUseBackendService);
    private readonly loginService = inject(LoginService);
    private readonly languageService = inject(LanguageService);
    private readonly browserService = inject(BrowserService);
    private readonly config = inject(CONFIG_TOKEN);

    public getTemplates() {
        return this.currentTemplate;
    }

    public setTemplate(header: TemplateRef<any>, footer: TemplateRef<any>): void {
        this.currentTemplate.header = header;
        this.currentTemplate.footer = footer;
    }

    public clearTemplate(): void {
        this.currentTemplate.header = null;
        this.currentTemplate.footer = null;
    }

    public async logoutUserWhenTermsOfUseNotAccepted(): Promise<void> {
        const isLoggedIn: boolean = await this.loginService.isLoggedIn();

        if (isLoggedIn) {
            const accepted: boolean = await this.userAcceptedTermsOfUse().catch(() => false);

            if (!accepted) {
                await this.loginService.logoutUser(true, false);
            }
        }
    }

    public getCurrentTermsOfUse(): Observable<TermsOfUseModel> {
        return this.termsOfUseBackendService.getCurrentTermsOfUse(this.config.API_VERSION);
    }

    public async getUserTermsOfUse(): Promise<TermsOfUseForUserModel> {
        return firstValueFrom(this.termsOfUseBackendService.getTermsOfUseForUser(this.config.API_VERSION, this.loginService.getToken()));
    }

    public async acceptTermsOfUse(eulaVersion: number, privacyPolicyVersion: number): Promise<void> {
        await firstValueFrom(
            this.termsOfUseBackendService.updateTermsOfUse(
                this.config.API_VERSION,
                this.loginService.getToken(),
                eulaVersion,
                privacyPolicyVersion,
            ),
        );
    }

    public openTerms(termOfUseModel: TermOfUseModel): void {
        const privacyPolicyUri = termOfUseModel.uri.replace('{culture}', this.languageService.getCulture().toLowerCase());
        this.openUri(privacyPolicyUri);
    }

    public openUri(uri: string): void {
        const bfsgUri = uri.replace('{culture}', this.languageService.getCulture().toLowerCase());
        this.browserService.open(bfsgUri).then();
    }

    private async userAcceptedTermsOfUse(): Promise<boolean> {
        return this.getUserTermsOfUse().then(
            (termsOfUseForUserModel) => termsOfUseForUserModel.isPrivacyPolicyValid && termsOfUseForUserModel.isEulaValid,
        );
    }
}
